/**
 * Moonshot mode detection.
 *
 * A request is in "moonshot mode" when the URL has `?mode=moonshot` (or the
 * path is `/moonshot`). This is the single source of truth that gates the
 * DemoMockProvider, the debug HUD, the voice-scripted chat flow, and the
 * auth-wall bypass.
 *
 * The path is read as the app's own (appPathname, 2026-10-18): inside the new
 * design's demonstration tree, /prest0design/moonshot is the demo's page too,
 * so the gate opens there as it does on prest0.ai/moonshot. It compared the
 * raw path, so the tree's page drew without the gate's provider.
 */
import { appPathname } from '../design/previewPath';

export interface MoonshotState {
  /** True when moonshot demo mode is active. */
  enabled: boolean;
  /** Current journey (1..6). Defaults to 1 when enabled and no explicit value. */
  journey: number;
  /** True when the debug HUD should be hidden (`&hud=off`). */
  hudOff: boolean;
}

export const JOURNEY_COUNT = 9;

export const JOURNEY_NAMES: Record<number, string> = {
  1: 'voice-chat',
  2: 'deep-research',
  3: 'document-reader',
  4: 'legal-draft',
  5: 'bilingual-flip',
  6: 'voice-calling',
  7: 'agent-builder',
  8: 'memory-recall',
  9: 'marketing-campaign',
};

export function parseMoonshotParams(search: string, pathname: string): MoonshotState {
  const params = new URLSearchParams(search || '');
  const modeParam = (params.get('mode') || '').toLowerCase();
  const path = appPathname(pathname || '');
  const onMoonshotPath = path === '/moonshot' || path.startsWith('/moonshot/');
  const enabled = modeParam === 'moonshot' || onMoonshotPath;

  let journey = 1;
  if (enabled) {
    const raw = parseInt(params.get('journey') || '1', 10);
    if (!Number.isNaN(raw) && raw >= 1 && raw <= JOURNEY_COUNT) {
      journey = raw;
    }
  }

  const hudOff = (params.get('hud') || '').toLowerCase() === 'off';

  return { enabled, journey, hudOff };
}

export function isMoonshotMode(): boolean {
  if (typeof window === 'undefined') return false;
  return parseMoonshotParams(window.location.search, window.location.pathname).enabled;
}

export function getMoonshotState(): MoonshotState {
  if (typeof window === 'undefined') {
    return { enabled: false, journey: 1, hudOff: false };
  }
  return parseMoonshotParams(window.location.search, window.location.pathname);
}
